import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export function ForwardLeadDialog({ lead, open, onOpenChange }) {
  const qc = useQueryClient();
  const [toUser, setToUser] = useState("");
  const [note, setNote] = useState("");
  const { data: users = [] } = useQuery({ queryKey: ["assignable"], queryFn: async () => (await api.get("/leads/assignable")).data, enabled: open });

  const mutate = useMutation({
    mutationFn: async () => (await api.post(`/leads/${lead.id}/forward`, { to_user_id: toUser, note: note || null })).data,
    onSuccess: () => {
      ["leads", "dashboard-stats"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      toast.success("Lead forwarded");
      onOpenChange(false); setToUser(""); setNote("");
    },
    onError: (e) => toast.error(apiError(e)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white dark:bg-slate-900" data-testid="forward-lead-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Forward / Assign — {lead?.name}</DialogTitle>
          <DialogDescription>Reassign this lead to another team member.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="space-y-2">
            <Label>Assign to *</Label>
            <Select value={toUser} onValueChange={setToUser}>
              <SelectTrigger data-testid="forward-user-select"><SelectValue placeholder="Select a team member" /></SelectTrigger>
              <SelectContent>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Note</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note for the assignee" data-testid="forward-note-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-blue-700 hover:bg-blue-800 text-white" disabled={!toUser || mutate.isPending} onClick={() => mutate.mutate()} data-testid="forward-save-button">Forward</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const DIST_STATUSES = ["New", "Contacted", "Not Connected", "Interested", "Follow-up"];

export function DistributeDialog({ open, onOpenChange }) {
  const qc = useQueryClient();
  const [targets, setTargets] = useState([]);
  const [status, setStatus] = useState("New");
  const [limit, setLimit] = useState("50");
  const [onlyMine, setOnlyMine] = useState(true);
  const { data: users = [] } = useQuery({ queryKey: ["assignable"], queryFn: async () => (await api.get("/leads/assignable")).data, enabled: open });

  const toggle = (id) => setTargets((t) => (t.includes(id) ? t.filter((x) => x !== id) : [...t, id]));

  const mutate = useMutation({
    mutationFn: async () => (await api.post("/leads/distribute", {
      target_user_ids: targets,
      status: status || null,
      only_owned_by_me: onlyMine,
      limit: limit ? Number(limit) : null,
    })).data,
    onSuccess: (data) => {
      ["leads", "dashboard-stats"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      toast.success(`Distributed ${data?.distributed ?? data?.count ?? ""} leads`.trim());
      onOpenChange(false); setTargets([]);
    },
    onError: (e) => toast.error(apiError(e)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white dark:bg-slate-900 max-w-lg" data-testid="distribute-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Distribute Leads</DialogTitle>
          <DialogDescription>Evenly assign a batch of leads across selected team members.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>From status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="distribute-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{DIST_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Max leads</Label>
              <input type="number" value={limit} onChange={(e) => setLimit(e.target.value)} data-testid="distribute-limit-input" className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
            <input type="checkbox" checked={onlyMine} onChange={(e) => setOnlyMine(e.target.checked)} data-testid="distribute-onlymine-checkbox" /> Only distribute leads owned by me
          </label>
          <div className="space-y-2">
            <Label>Assign to ({targets.length} selected)</Label>
            <div className="max-h-48 overflow-y-auto border border-slate-200 dark:border-slate-700 rounded-lg p-2 space-y-1">
              {users.map((u) => (
                <label key={u.id} className="flex items-center gap-2 text-sm px-2 py-1.5 rounded hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer" data-testid={`distribute-user-${u.id}`}>
                  <input type="checkbox" checked={targets.includes(u.id)} onChange={() => toggle(u.id)} />
                  <span className="font-medium text-slate-800 dark:text-slate-200">{u.name}</span>
                  <span className="text-xs text-slate-400">{u.role}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-blue-700 hover:bg-blue-800 text-white" disabled={targets.length === 0 || mutate.isPending} onClick={() => mutate.mutate()} data-testid="distribute-save-button">Distribute</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
